import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import RemoteSourceForm, { RemoteSourceFormData } from 'components/forms/remote-source/RemoteSourceForm';
import panic from 'errors/panic';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { REMOTE_SOURCES_PAGE_PATH } from 'routes/paths';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

/**
 * Page used to edit a remote source.
 */
export default function EditRemoteSourcePage() {
  const navigate = useNavigate();
  const { remoteSourceId } = useParams();
  const { getAction } = useApi();

  const { data, loading, error } = useImmediateAction(() => {
    const remoteSourceGetAction = getAction('RemoteSourceGet');
    return remoteSourceGetAction({ parameters: { remoteSourceId: remoteSourceId! } });
  });

  if (error) {
    panic(error);
    return <></>;
  }

  if (!remoteSourceId) {
    return <Navigate to={REMOTE_SOURCES_PAGE_PATH.original} />;
  }

  /**
   * Handles the form submission.
   */
  function onSubmit({ name, type, url, login, password, incomingApiKey, userId }: RemoteSourceFormData) {
    const remoteSourceUpdateAction = getAction('RemoteSourceUpdate');

    return remoteSourceUpdateAction({
      payload: {
        name,
        type,
        url,
        login,
        password,
        incomingApiKey,
        userId: userId ? Number(userId) : undefined,
      },
      parameters: {
        remoteSourceId: remoteSourceId!,
      },
    })
      .then(() => {
        notifications.show({
          title: 'Externý zdroj bol upravený',
          message: `Externý zdroj ${name} bol úspešne upravený.`,
          color: SUCCESS_NOTIFICATION_COLOR,
        });

        navigate(REMOTE_SOURCES_PAGE_PATH.original);
      })
      .catch(panic);
  }

  return (
    <DashboardLayout
      title={`Úprava externého zdroja: ${data?.name ?? ''}`}
      breadcrumbs={[
        { title: 'Externé zdroje', link: REMOTE_SOURCES_PAGE_PATH.original },
        { title: data?.name ?? 'Načítavanie ...' },
      ]}
    >
      {!loading && (
        <RemoteSourceForm
          initialValues={{
            name: data.name,
            type: data.type,
            url: data.url,
            login: data.login,
            password: data.password,
            incomingApiKey: data.incomingApiKey,
            userId: data.user ? String(data.user.userId) : undefined,
          }}
          readOnly={{
            type: true,
          }}
          onSubmit={onSubmit}
        />
      )}
    </DashboardLayout>
  );
}
